// Authenticated company auth + thread store. Conversation metadata and
// messages come from the signed-in user's server-owned workspace. The client
// keeps only a short-lived in-memory read cache for the mounted runtime; it is
// never a second history store and is discarded with the page.

import { withMessageContext } from './lib/message-context';
import type { ThreadMeta } from './thread-types';
import type { UIMessage } from 'ai';
import { parseComposerHighlights, type ComposerHighlight } from './lib/composer-highlights';
import { traceHistory } from './lib/history-trace';
import { withRequestTimeout } from './lib/request-timeout';
import {
  apiUrl,
  authHeaders,
  clearSessionToken,
  isNativeShell,
  setSessionToken,
} from './lib/shell';
import { SPEECH_UNAVAILABLE, type SpeechAvailability } from './speech/contracts';
import { answerReaderAdmitted } from './lib/answer-resume';

export type UserRole = 'user' | 'developer' | 'admin';

export interface SessionUser {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
}

interface ServerMessage {
  id: string;
  role: string;
  parts: unknown[];
  metadata?: unknown;
  parentId?: string | null;
  branchBound?: boolean;
  /** Collaboration v1: the reader whose suggested follow-up this user
   * message asked, from the server's suggestion row (never the parts). */
  suggestedBy?: { userId: string; name: string | null };
}

type ServerBranch = { headId: string | null; parents: Map<string, string | null> };
const serverBranches = new Map<string, ServerBranch>();
export function loadServerThreadBranch(threadId: string): ServerBranch | null {
  return serverBranches.get(threadId) ?? null;
}

/** The server's current selected parent, including answers completed after
 * the last local read. Native error messages are not durable parent IDs. */
export async function currentServerThreadHead(
  threadId: string,
  isCurrentOwner: () => boolean,
): Promise<string | null> {
  if (!isCurrentOwner()) throw new DOMException('Session changed', 'AbortError');
  await json(await post('/api/v1/threads', { id: threadId }));
  const result = await json<{ headId: string | null }>(
    await withRequestTimeout(15_000, (signal) =>
      apiFetch(`/api/v1/threads/${threadId}/head`, { signal }),
    ),
  );
  if (!isCurrentOwner()) throw new DOMException('Session changed', 'AbortError');
  if (result.headId !== null && typeof result.headId !== 'string')
    throw new Error('This chat is unavailable. Reload it before sending.');
  const branch = serverBranches.get(threadId) ?? { headId: null, parents: new Map() };
  branch.headId = result.headId;
  serverBranches.set(threadId, branch);
  return result.headId;
}

/** Whose private account a tool result came from, when it is not the
 * reader's own ("From Anna's Gmail"; collaboration v1). */
export interface ToolSource {
  userId: string;
  name: string | null;
  provider: string | null;
}
export type ToolSources = Record<string, ToolSource>;

/** What a run chat's card says about its run (server agents/run-card.mjs). */
export interface AgentRunCard {
  agentName: string;
  triggerType: string;
  agentRevision: number | null;
  runBy: { userId: string; name: string | null } | null;
  skills: { name: string; revision: number }[];
  createdAt: string | null;
  scheduledFor: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  /** The credits the run used, only for the person it acted for and whoever manages billing. */
  credits?: number;
  /** The connections the run could not read: every call that used one failed. */
  unavailableSources?: string[];
}

/** Who can read a chat and how the signed-in person may use it
 * (collaboration v1). `audience` and `sharingRevision` only for the owner. */
export interface ServerThreadMeta {
  id: string;
  title: string;
  access: 'owner' | 'viewer';
  ownerName: string | null;
  visibility: 'private' | 'members' | 'company';
  audience?: { userId: string; role: 'viewer' | 'editor' }[];
  sharingRevision?: number;
  agentRunId: string | null;
  /** Projects v1: the project this chat lives in, when the reader can read it. */
  project?: import('./workspace/projects/types').ProjectPlace | null;
  /** The run record of a run chat: the only source for how the run ended. Every reader gets the
   * status, whether a Result exists, the run card the chat opens with and the ids of the run's
   * internal messages it stands for; the rest only the person who ran it. */
  agentRun: {
    status: string;
    hasResult: boolean;
    card?: AgentRunCard;
    internalMessageIds: string[];
    agentId?: string;
    errorCode?: string | null;
    error?: string | null;
  } | null;
  forkedFrom: {
    threadId: string;
    messageId: string | null;
    title: string | null;
    ownerName: string | null;
  } | null;
  /** User message id → who suggested the follow-up it asked. */
  suggestedBy: Record<string, { userId: string; name: string | null }>;
  /** Tool call id → whose private account its result came from. */
  toolSources: ToolSources;
}

const serverThreadCache = new Map<string, ServerMessage[]>();
export function hasServerThreadMessage(threadId: string, messageId: string): boolean {
  return serverThreadCache.get(threadId)?.some((message) => message.id === messageId) ?? false;
}
const serverThreadMeta = new Map<string, ServerThreadMeta>();
const threadMetaListeners = new Set<() => void>();

const ACCESS = new Set(['owner', 'viewer']);
const VISIBILITY = new Set(['private', 'members', 'company']);

/** Only well-formed entries: a tool call id → { userId, name, provider }. */
export function toolSourcesFrom(value: unknown): ToolSources {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const sources: ToolSources = {};
  for (const [id, entry] of Object.entries(value as Record<string, unknown>)) {
    const source = entry as Partial<ToolSource> | null;
    if (!source || typeof source.userId !== 'string') continue;
    sources[id] = {
      userId: source.userId,
      name: typeof source.name === 'string' ? source.name : null,
      provider: typeof source.provider === 'string' ? source.provider : null,
    };
  }
  return sources;
}

// The run card, when the server sent a well-formed one (an older server sends none).
function runCard(value: unknown): AgentRunCard | null {
  const card = value as Record<string, unknown> | null;
  if (!card || typeof card.agentName !== 'string' || typeof card.triggerType !== 'string')
    return null;
  const time = (key: string) => (typeof card[key] === 'string' ? (card[key] as string) : null);
  const runBy = card.runBy as AgentRunCard['runBy'];
  return {
    agentName: card.agentName,
    triggerType: card.triggerType,
    agentRevision: typeof card.agentRevision === 'number' ? card.agentRevision : null,
    runBy: runBy && typeof runBy.userId === 'string' ? runBy : null,
    skills: Array.isArray(card.skills)
      ? (card.skills as AgentRunCard['skills']).filter(
          (skill) => typeof skill?.name === 'string' && typeof skill.revision === 'number',
        )
      : [],
    createdAt: time('createdAt'),
    scheduledFor: time('scheduledFor'),
    startedAt: time('startedAt'),
    finishedAt: time('finishedAt'),
    durationMs: typeof card.durationMs === 'number' ? card.durationMs : null,
    ...(typeof card.credits === 'number' ? { credits: card.credits } : {}),
    ...(Array.isArray(card.unavailableSources)
      ? {
          unavailableSources: card.unavailableSources.filter(
            (name): name is string => typeof name === 'string',
          ),
        }
      : {}),
  };
}

// Only a well-formed collaboration answer becomes meta; anything else (an
// older server) leaves the chat an ordinary owned chat.
function runRecord(value: unknown): ServerThreadMeta['agentRun'] {
  const run = value as Record<string, unknown> | null;
  if (!run || typeof run.status !== 'string' || typeof run.hasResult !== 'boolean') return null;
  const text = (key: string) => (typeof run[key] === 'string' ? (run[key] as string) : null);
  const card = runCard(run.card);
  return {
    status: run.status,
    hasResult: run.hasResult,
    ...(card ? { card } : {}),
    internalMessageIds: Array.isArray(run.internalMessageIds)
      ? run.internalMessageIds.filter((id): id is string => typeof id === 'string')
      : [],
    ...(typeof run.agentId === 'string'
      ? { agentId: run.agentId, errorCode: text('errorCode'), error: text('error') }
      : {}),
  };
}

const PROJECT_TINTS = new Set(['sand', 'green', 'blue', 'plain']);
/** Projects v1: where the chat lives. Absent from an older server, null outside a project. */
function projectFrom(value: unknown): Pick<ServerThreadMeta, 'project'> {
  if (value === null) return { project: null };
  if (!value || typeof value !== 'object') return {};
  const place = value as { id?: unknown; name?: unknown; tint?: unknown };
  if (typeof place.id !== 'string' || typeof place.name !== 'string') return {};
  const tint = PROJECT_TINTS.has(place.tint as string) ? (place.tint as 'sand') : 'plain';
  return { project: { id: place.id, name: place.name, tint } };
}

function threadMetaFrom(thread: Record<string, unknown>, messages: ServerMessage[]) {
  if (!ACCESS.has(thread.access as string) || !VISIBILITY.has(thread.visibility as string))
    return null;
  const suggestedBy: ServerThreadMeta['suggestedBy'] = {};
  for (const message of messages)
    if (message.suggestedBy?.userId) suggestedBy[message.id] = message.suggestedBy;
  return {
    id: String(thread.id),
    title: typeof thread.title === 'string' ? thread.title : 'New chat',
    access: thread.access as ServerThreadMeta['access'],
    ownerName: typeof thread.ownerName === 'string' ? thread.ownerName : null,
    visibility: thread.visibility as ServerThreadMeta['visibility'],
    ...(Array.isArray(thread.audience)
      ? { audience: thread.audience as NonNullable<ServerThreadMeta['audience']> }
      : {}),
    ...(typeof thread.sharingRevision === 'number'
      ? { sharingRevision: thread.sharingRevision }
      : {}),
    agentRunId: typeof thread.agentRunId === 'string' ? thread.agentRunId : null,
    agentRun: runRecord(thread.agentRun),
    forkedFrom: (thread.forkedFrom as ServerThreadMeta['forkedFrom']) ?? null,
    suggestedBy,
    toolSources: toolSourcesFrom(thread.toolSources),
    ...projectFrom(thread.project),
  } satisfies ServerThreadMeta;
}

/** The loaded chat's collaboration meta, or null for an ordinary owned chat. */
export function loadServerThreadMeta(threadId: string): ServerThreadMeta | null {
  return serverThreadMeta.get(threadId) ?? null;
}

/** Re-render readers of thread meta when a chat is re-read (after a share). */
export function subscribeThreadMeta(listener: () => void): () => void {
  threadMetaListeners.add(listener);
  return () => threadMetaListeners.delete(listener);
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

// All server calls go through here: apiUrl points at the absolute origin in
// the native shells (identity on the web), and authHeaders carries the shell's
// bearer session (empty on the web, where the cookie + same-origin does it).
function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(apiUrl(path), {
    credentials: 'same-origin',
    ...init,
    headers: { ...authHeaders(), ...(init.headers ?? {}) },
  });
}

// ── Auth client ───────────────────────────────────────────────────────────────

const SPEECH_REASONS: ReadonlySet<string> = new Set(['not_configured', 'provider_unavailable']);

/**
 * `speech` in /api/config (C-3). Anything missing or malformed fails closed to
 * unavailable, so the microphone is disabled rather than recording and failing.
 * Each mode, including BUILD 2's `voice`, `handsFree` and `readAloud`, is on
 * only when it is exactly `true`.
 */
export function parseSpeechAvailability(value: unknown): SpeechAvailability {
  if (!value || typeof value !== 'object') return SPEECH_UNAVAILABLE;
  const speech = value as Record<string, unknown>;
  const modes = (speech.modes && typeof speech.modes === 'object' ? speech.modes : {}) as Record<
    string,
    unknown
  >;
  if (typeof speech.available !== 'boolean') return SPEECH_UNAVAILABLE;
  const reason =
    typeof speech.reason === 'string' && SPEECH_REASONS.has(speech.reason)
      ? (speech.reason as SpeechAvailability['reason'])
      : speech.reason === null
        ? null
        : 'unknown';
  return {
    available: speech.available,
    reason: speech.available ? null : (reason ?? 'unknown'),
    modes: {
      dictation: modes.dictation === true,
      partials: modes.partials === true,
      meetingPreview: modes.meetingPreview === true,
      meetingRecord: modes.meetingRecord === true,
      voice: modes.voice === true,
      handsFree: modes.handsFree === true,
      readAloud: modes.readAloud === true,
    },
  };
}

/**
 * Serving config the UI needs before the first turn. `composerHighlights` is
 * the server-owned list of special text and activation rules. The highlight
 * list fails closed on error so an unavailable config never invents client-side
 * command behavior; `speech` fails closed to unavailable the same way.
 */
export async function fetchConfig(): Promise<{
  billingRequired: boolean | null;
  composerHighlights: ComposerHighlight[];
  speech: SpeechAvailability;
  answerReaderAdmitted: boolean;
  chatAttachments: boolean;
}> {
  try {
    const res = await apiFetch('/api/config');
    if (!res.ok)
      return {
        billingRequired: null,
        composerHighlights: [],
        speech: SPEECH_UNAVAILABLE,
        answerReaderAdmitted: false,
        chatAttachments: false,
      };
    const body = await res.json();
    return {
      billingRequired: typeof body.billingRequired === 'boolean' ? body.billingRequired : null,
      composerHighlights: parseComposerHighlights(body.composerHighlights),
      speech: parseSpeechAvailability(body.speech),
      answerReaderAdmitted: answerReaderAdmitted(body),
      chatAttachments: body.chatAttachments === true,
    };
  } catch {
    return {
      billingRequired: null,
      composerHighlights: [],
      speech: SPEECH_UNAVAILABLE,
      answerReaderAdmitted: false,
      chatAttachments: false,
    };
  }
}

/**
 * Auth surface discovery: which OAuth providers the server exposes. Company
 * admission is the only product surface; the client never selects a mode.
 * Fail-soft to the password surface so the auth screen still works if this
 * request errors.
 */
export interface AuthConfig {
  oauth: string[];
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  try {
    const res = await apiFetch('/api/v1/auth/config');
    if (!res.ok) return { oauth: [] };
    const body = await res.json();
    return {
      oauth: Array.isArray(body.oauth)
        ? body.oauth.filter((p: unknown) => typeof p === 'string')
        : [],
    };
  } catch {
    return { oauth: [] };
  }
}

export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const res = await apiFetch('/api/v1/auth/me');
    if (!res.ok) return null;
    return (await res.json()).user as SessionUser;
  } catch {
    return null;
  }
}

/** Resolve an OAuth return before listing the authenticated workspace. */
export async function resumeOAuthSession(): Promise<SessionUser | null> {
  return fetchMe();
}

async function post(path: string, body: unknown, init: RequestInit = {}): Promise<Response> {
  // apiFetch already merges authHeaders(); only add the content type here.
  return apiFetch(path, {
    method: 'POST',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    body: JSON.stringify(body),
  });
}

// Login/signup responses carry `session_token` (same value as the cookie) for
// the native shells; setSessionToken adopts it there and no-ops on the web.
type AuthResponse = { user: SessionUser; session_token?: string };

/**
 * Credential failure with a stable machine code — the auth screen maps codes
 * to localized copy (ui/components/auth/strings.ts) instead of this layer
 * hard-coding one language.
 */
export class AuthError extends Error {
  code: string;
  constructor(code: string) {
    super(code);
    this.name = 'AuthError';
    this.code = code;
  }
}

const authFailureCode = (status: number): string | null => {
  if (status === 409) return 'email_taken';
  if (status === 401) return 'bad_credentials';
  if (status === 429) return 'rate_limited';
  if (status === 403) return 'invalid_invite';
  if (status === 400) return 'invalid_input';
  if (status === 410) return 'verification_expired';
  if (status === 412) return 'verification_password_required';
  if (status === 422) return 'account_link_conflict';
  if (status === 428) return 'account_link_expired';
  return status >= 500 ? 'server_unavailable' : null;
};

async function completeAuth(res: Response): Promise<SessionUser> {
  const code = res.ok ? null : await signupFailureCode(res);
  if (code) throw new AuthError(code);
  const { user, session_token } = await json<AuthResponse>(res);
  setSessionToken(session_token ?? null);
  return user;
}

export interface PendingSignup {
  verificationRequired: true;
  email: string;
  expiresAt: string;
}

async function pendingSignupFrom(response: Response): Promise<PendingSignup> {
  const pending = await json<{
    verification_required: true;
    email: string;
    expires_at: string;
  }>(response);
  return {
    verificationRequired: true,
    email: pending.email,
    expiresAt: pending.expires_at,
  };
}

/**
 * Start password signup; account creation waits for inbox verification. The
 * locale is the auth door's language, so the verification email matches it.
 */
export async function signup(
  email: string,
  password: string,
  inviteCode?: string,
  companyName?: string,
  locale?: string,
): Promise<PendingSignup> {
  const body = { email, password, invite_code: inviteCode, company_name: companyName, locale };
  const response = await post('/api/v1/auth/signup', body);
  const code = response.ok ? null : await signupFailureCode(response);
  if (code) throw new AuthError(code);
  return pendingSignupFrom(response);
}

/**
 * Send the verification email again from the browser that started the
 * signup (the HttpOnly pending cookie proves it). The earlier link stops
 * working; `verification_expired` means the pending signup is gone.
 */
export async function resendSignupVerification(
  email: string,
  locale?: string,
): Promise<PendingSignup> {
  const response = await post('/api/v1/auth/signup/resend', { email, locale });
  const code = response.ok ? null : authFailureCode(response.status);
  if (code) throw new AuthError(code);
  if (!response.ok) throw new AuthError('server_unavailable');
  return pendingSignupFrom(response);
}

async function signupFailureCode(response: Response): Promise<string | null> {
  if (response.status === 403) {
    const body = (await response.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === 'work_email_required') return 'work_email_required';
  }
  if (response.status === 400) {
    const body = (await response.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === 'company_name_required') return 'company_name_required';
    if (body.error === 'invalid_company_details') return 'invalid_company_details';
    if (body.error === 'password_too_short') return 'password_too_short';
    if (body.error === 'password_too_long') return 'password_too_long';
    if (body.error === 'password_compromised') return 'password_compromised';
    if (body.error === 'email_invalid') return 'email_invalid';
  }
  return authFailureCode(response.status);
}

/**
 * Finish password signup from the fragment-only verification email link.
 * In the signup browser the HttpOnly pending cookie is proof enough; a link
 * opened on another device gets `verification_password_required` (412) and
 * retries with the password chosen at signup.
 */
export async function completePasswordSignup(
  token: string,
  password?: string,
): Promise<SessionUser> {
  const body = password ? { token, password } : { token };
  return completeAuth(await post('/api/v1/auth/signup/complete', body));
}

/** Log in; optionally consume a staged Google proof after password verification. */
export async function login(
  email: string,
  password: string,
  linkGoogle = false,
): Promise<SessionUser> {
  const body = linkGoogle ? { email, password, link_provider: 'google' } : { email, password };
  return completeAuth(await post('/api/v1/auth/login', body));
}

/** Request recovery without exposing whether the email has a password account. */
export async function requestPasswordReset(email: string, locale?: string): Promise<void> {
  const response = await post('/api/v1/auth/password/reset/request', { email, locale });
  if (!response.ok) {
    throw new AuthError(
      response.status === 400
        ? 'invalid_input'
        : response.status === 429
          ? 'rate_limited'
          : 'server_unavailable',
    );
  }
}

/** Consume a fragment-only recovery token. Success intentionally stays signed out. */
export async function completePasswordReset(
  token: string,
  newPassword: string,
  locale?: string,
): Promise<void> {
  const response = await post('/api/v1/auth/password/reset/complete', {
    token,
    new_password: newPassword,
    locale,
  });
  if (!response.ok) throw new AuthError(await passwordLifecycleFailure(response));
  setSessionToken(null);
}

export async function fetchPasswordStatus(): Promise<boolean> {
  const response = await apiFetch('/api/v1/auth/password/status');
  if (!response.ok)
    throw new AuthError(response.status === 401 ? 'not_authenticated' : 'server_unavailable');
  return Boolean((await json<{ has_password: boolean }>(response)).has_password);
}

/** Change a known password. The server revokes every session, including this one. */
export async function changeAccountPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const response = await post('/api/v1/auth/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  if (!response.ok) throw new AuthError(await passwordLifecycleFailure(response));
  setSessionToken(null);
}

async function passwordLifecycleFailure(response: Response): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { error?: unknown };
  const error = typeof body.error === 'string' ? body.error : '';
  if (response.status === 410) return 'reset_expired';
  if (response.status === 429) return 'rate_limited';
  if (response.status === 401) return 'current_password_wrong';
  if (response.status === 409 && error.includes('does not use')) return 'password_unavailable';
  if (response.status === 409) return 'password_changed';
  if (error.includes('different')) return 'password_reused';
  if (error === 'password_too_short') return 'password_too_short';
  if (error === 'password_too_long') return 'password_too_long';
  if (error === 'password_compromised') return 'password_compromised';
  return response.status >= 500 ? 'server_unavailable' : 'invalid_input';
}

/** Create a confidential OAuth browser transaction and return Google's URL. */
export async function beginGoogleSignIn(
  inviteCode?: string,
  companyName?: string,
): Promise<string> {
  const body = { invite_code: inviteCode, company_name: companyName };
  const response = await post('/api/v1/auth/google/start', body);
  const code = response.ok ? null : await signupFailureCode(response);
  if (code) throw new AuthError(code);
  const { url } = await json<{ url: string }>(response);
  return url;
}

/** Create an opaque Android system-browser start URL bound to the app's PKCE proof. */
export async function beginNativeGoogleSignIn(
  challenge: string,
  inviteCode?: string,
  companyName?: string,
): Promise<string> {
  const body = { challenge, invite_code: inviteCode, company_name: companyName };
  const response = await post('/api/v1/auth/google/native/start', body);
  const code = response.ok ? null : await signupFailureCode(response);
  if (code) throw new AuthError(code);
  const { url } = await json<{ url: string }>(response);
  return url;
}

/** Exchange the Android deep-link handoff and adopt its bearer session. */
export async function completeNativeGoogleSignIn(
  code: string,
  verifier: string,
): Promise<SessionUser> {
  const response = await post('/api/v1/auth/google/native/exchange', { code, verifier });
  if (!response.ok) {
    throw new AuthError(response.status === 429 ? 'rate_limited' : 'expired');
  }
  return completeAuth(response);
}

/** Log out on this device only. On the web the session is the cookie the server clears. A native shell's session
 * is its stored bearer, which that answer does not end (it stays valid on the server): it leaves this device first,
 * whatever the answer, and a copy that could not be removed rejects (SessionTokenKeptError). */
export async function logout(): Promise<void> {
  if (isNativeShell()) {
    await clearSessionToken();
    await post('/api/v1/auth/logout', {}).catch(() => undefined);
    return;
  }
  const response = await post('/api/v1/auth/logout', {});
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
}

/** Log out on every device, this one included. */
export async function logoutEverywhere(): Promise<void> {
  const response = await post('/api/v1/auth/sessions/revoke-all', {});
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  // Every session is revoked, this device's included: a stored copy that could not be removed opens nothing.
  await clearSessionToken().catch(() => {});
}

/** Permanently delete the signed-in account, then discard the native bearer. */
export async function deleteAccount(): Promise<void> {
  const response = await apiFetch('/api/v1/auth/account', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmation: 'delete' }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  setSessionToken(null);
}

// ── Server thread index ────────────────────────────────────────────────────────

export async function listServerThreads(): Promise<ThreadMeta[]> {
  const { threads } = await json<{ threads: ThreadMeta[] }>(await apiFetch('/api/v1/threads'));
  return threads.sort((a, b) => b.updatedAt - a.updatedAt);
}

const THREAD_DELETE_TIMEOUT_MS = 15_000;

export async function deleteServerThread(id: string): Promise<void> {
  // Block new mirrors first, then let every already-enqueued message finish.
  // Otherwise mirrorOne's create-if-missing POST can land after DELETE and
  // resurrect the conversation. Mirrors arriving during deletion wait for its
  // outcome: success discards them, while failure replays them in queue order.
  let resolveDelete!: (deleted: boolean) => void;
  const outcome = new Promise<boolean>((resolve) => {
    resolveDelete = resolve;
  });
  const gate = { outcome };
  deleteGates.set(id, gate);
  try {
    await (mirrorTails.get(id) ?? Promise.resolve()).catch(() => {});
    const response = await withRequestTimeout(THREAD_DELETE_TIMEOUT_MS, (signal) =>
      apiFetch(`/api/v1/threads/${id}`, { method: 'DELETE', signal }),
    );
    // A retry after an ambiguous/lost successful response reaches an already
    // absent thread. DELETE is idempotent from the user's perspective, so 404
    // completes the same tombstone path instead of replaying queued mirrors.
    if (!response.ok && response.status !== 404) throw new Error(`HTTP ${response.status}`);
    deletedThreadIds.add(id);
    serverThreadCache.delete(id);
    serverBranches.delete(id);
    serverThreadMeta.delete(id);
    pendingServerTitles.delete(id);
    resolveDelete(true);
  } catch (error) {
    resolveDelete(false);
    throw error;
  } finally {
    if (deleteGates.get(id) === gate) deleteGates.delete(id);
  }
}

/**
 * Load the authenticated thread into the mounted runtime's in-memory cache.
 * Ownership is enforced by the server request; the callback additionally
 * prevents a response from a retired session being adopted by the next one.
 */
export async function seedServerThread(
  id: string,
  isCurrentOwner: () => boolean = () => true,
): Promise<void> {
  if (!isCurrentOwner()) throw new DOMException('Session changed', 'AbortError');
  const { thread } = await json<{
    thread: { messages: ServerMessage[] } & Record<string, unknown>;
  }>(await withRequestTimeout(15_000, (signal) => apiFetch(`/api/v1/threads/${id}`, { signal })));
  if (!isCurrentOwner()) throw new DOMException('Session changed', 'AbortError');
  if (deletedThreadIds.has(id)) return;
  serverThreadCache.set(id, thread.messages ?? []);
  if (typeof thread.headId === 'string') {
    const parents = new Map<string, string | null>();
    for (const message of thread.messages ?? []) {
      if (
        message.branchBound === true &&
        (message.parentId === null || typeof message.parentId === 'string')
      )
        parents.set(message.id, message.parentId);
    }
    serverBranches.set(id, { headId: thread.headId, parents });
  } else serverBranches.delete(id);
  const meta = threadMetaFrom(thread, thread.messages ?? []);
  if (meta) serverThreadMeta.set(id, meta);
  else serverThreadMeta.delete(id);
  threadMetaListeners.forEach((listener) => listener());
}

/** Return messages already loaded for the mounted authenticated thread. */
export function loadServerThreadMessages(threadId: string): UIMessage[] {
  return (serverThreadCache.get(threadId) ?? []).map((message) => ({
    id: message.id,
    role: message.role as UIMessage['role'],
    parts: message.parts as UIMessage['parts'],
    ...(message.metadata === undefined ? {} : { metadata: message.metadata }),
  }));
}

// Cap each mirror request. Because mirrors are now serialized per thread (see
// mirrorMessage), a single hung request would otherwise block every later turn's
// mirror behind it; a timeout bounds that blast radius — the aborted write is
// dropped (best-effort) and the queue moves on.
const MIRROR_TIMEOUT_MS = 15_000;
// A finished answer is the one write nothing else recreates, and a busy server can exceed the cap above
// (B301: the run-end thread write timed out after 15 s and the answer was never persisted). Best-effort
// writes therefore retry a timeout, network error or 408/425/429/5xx a few times. The writes are
// idempotent upserts on the thread and message id; a submitted (`required`) write is never retried here.
export const mirrorRetry = { delaysMs: [2_000, 5_000, 10_000] };
const transientStatus = (status: number) =>
  status === 408 || status === 425 || status === 429 || status >= 500;
const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** null: the owner changed or the thread was deleted while waiting to retry; nothing more is sent. */
async function mirrorRequest(
  threadId: string,
  options: MirrorOptions,
  facts: Record<string, string | number | boolean | null>,
  send: (signal: AbortSignal) => Promise<Response>,
): Promise<Response | null> {
  for (let attempt = 0; ; attempt++) {
    const last = options.required || attempt >= mirrorRetry.delaysMs.length;
    try {
      const response = await withRequestTimeout(MIRROR_TIMEOUT_MS, send);
      if (response.ok || last || !transientStatus(response.status)) return response;
      traceHistory(threadId, 'mirror_retry', {
        ...facts,
        attempt: attempt + 1,
        status: response.status,
      });
    } catch (error) {
      if (last) throw error;
      traceHistory(threadId, 'mirror_retry', {
        ...facts,
        attempt: attempt + 1,
        error: error instanceof Error ? error.name : 'unknown',
      });
    }
    await pause(mirrorRetry.delaysMs[attempt]);
    if (deletedThreadIds.has(threadId) || !requireMirrorOwner(options)) {
      traceHistory(threadId, 'owner_changed', facts);
      return null;
    }
  }
}

// Model-generated titles waiting to ride the next message mirror of their
// thread (see mirrorThreadTitle). The title arrives mid-stream, after the
// submitted user's durable write and before run-end mirrors. It rides the
// next thread upsert even when that mirror skips its already-persisted user.
const pendingServerTitles = new Map<string, string>();

/**
 * Record the model-generated title for a signed-in thread. It is sent with the
 * thread's next mirror, including an otherwise deduplicated submitted user.
 */
export function mirrorThreadTitle(threadId: string, title: string): void {
  if (deletedThreadIds.has(threadId)) return;
  const clean = title.replace(/\s+/g, ' ').trim();
  if (clean) pendingServerTitles.set(threadId, clean);
}

async function mirrorOne(
  threadId: string,
  message: {
    id: string;
    role: string;
    parts: unknown[];
    parentId?: string | null;
    expectedHeadId?: string | null;
  },
  options: MirrorOptions,
): Promise<void> {
  const { required = false, skipMessage = false } = options;
  const facts = { messageId: message.id, role: message.role };
  if (!requireMirrorOwner(options)) {
    traceHistory(threadId, 'owner_changed', facts);
    return;
  }
  const title = pendingServerTitles.get(threadId);
  if (skipMessage && !title) return;
  try {
    // Ensure the thread row exists (idempotent), then append the message. The
    // title travels on the create/upsert; the server keeps it on later
    // title-less upserts and appendMessage only seeds the placeholder.
    const created = await mirrorRequest(threadId, options, facts, (signal) =>
      post('/api/v1/threads', title ? { id: threadId, title } : { id: threadId }, { signal }),
    );
    if (!created) return;
    if (!created.ok) {
      traceHistory(threadId, 'thread_upsert_failed', { ...facts, status: created.status });
      if (required) throw new Error(`HTTP ${created.status}`);
      return;
    }
    if (!requireMirrorOwner(options)) {
      traceHistory(threadId, 'owner_changed_after_thread_upsert', facts);
      return;
    }
    if (title && pendingServerTitles.get(threadId) === title) pendingServerTitles.delete(threadId);
    if (skipMessage) return;
    const appended = await mirrorRequest(threadId, options, facts, (signal) =>
      post(`/api/v1/threads/${threadId}/messages`, message, { signal }),
    );
    if (!appended) return;
    if (!appended.ok) {
      traceHistory(threadId, 'message_post_failed', { ...facts, status: appended.status });
      if (required) throw new Error(`HTTP ${appended.status}`);
      return;
    }
    if (message.parentId !== undefined) {
      const result = await appended
        .clone()
        .json()
        .catch(() => null);
      const branch = serverBranches.get(threadId) ?? {
        headId: null,
        parents: new Map<string, string | null>(),
      };
      branch.parents.set(message.id, message.parentId);
      if (typeof result?.message?.headId === 'string') branch.headId = result.message.headId;
      serverBranches.set(threadId, branch);
    }
    const cached = serverThreadCache.get(threadId) ?? [];
    const index = cached.findIndex((item) => item.id === message.id);
    const next = { ...message };
    if (index >= 0) cached[index] = next;
    else cached.push(next);
    serverThreadCache.set(threadId, cached);
    traceHistory(threadId, 'mirrored', { ...facts, status: appended.status });
    requireMirrorOwner(options);
  } catch (error) {
    traceHistory(threadId, 'mirror_error', {
      ...facts,
      error: error instanceof Error ? error.name : 'unknown',
    });
    if (required) throw error;
    /* Best effort — a title that did not land stays pending for the next
       authenticated mirror. */
  }
}

// Per-thread serialization queue for mirrored writes. History persistence fires
// at RUN END, where the runtime appends the finished [user, assistant] pair back
// to back and the app's history adapter mirrors each with a fire-and-forget
// `void mirrorMessage(...)`. Those two chains would otherwise race their
// `POST …/messages` inserts, and the server orders getThread() messages by
// `created_at`, so a lost race reloads the assistant ABOVE the user (~36% of
// turns, measured). Chaining per thread makes the inserts commit in append
// order — the user's row lands, with an earlier `created_at`, before the
// assistant's — while keeping the caller non-blocking (the enqueue is sync).
const mirrorTails = new Map<string, Promise<unknown>>();
const deleteGates = new Map<string, { outcome: Promise<boolean> }>();
const deletedThreadIds = new Set<string>();
type MirrorOptions = {
  required?: boolean;
  skipMessage?: boolean;
  isCurrentOwner?: () => boolean;
};

function requireMirrorOwner({ required, isCurrentOwner }: MirrorOptions): boolean {
  if (!isCurrentOwner || isCurrentOwner()) return true;
  if (required) throw new DOMException('Session changed', 'AbortError');
  return false;
}

function deletedMirror({ required }: MirrorOptions): Promise<void> {
  return required
    ? Promise.reject(new DOMException('Thread was deleted', 'AbortError'))
    : Promise.resolve();
}

async function requireUndeletedThread(threadId: string): Promise<void> {
  const gate = deleteGates.get(threadId);
  if ((gate && (await gate.outcome)) || deletedThreadIds.has(threadId)) {
    await deletedMirror({ required: true });
  }
}

/**
 * Mirror a message to the server (best effort unless `required` for submission).
 * Serialized per thread so concurrent fire-and-forget calls persist in the
 * order they were enqueued (see mirrorTails). `skipMessage` is only for a
 * message already retained by the server or successfully mirrored by this adapter;
 * pending titles still travel, through the same queue and deletion gates.
 */
export function mirrorMessage(
  threadId: string,
  rawMessage: {
    id: string;
    role: string;
    parts: unknown[];
    parentId?: string | null;
    expectedHeadId?: string | null;
  },
  options: MirrorOptions = {},
): Promise<void> {
  // What the person attached to this message (Select, files, sources) is written with it, once.
  const message = withMessageContext(threadId, rawMessage);
  if (deletedThreadIds.has(threadId)) return deletedMirror(options);
  const deleteGate = deleteGates.get(threadId);
  if (deleteGate) {
    return deleteGate.outcome.then((deleted) =>
      deleted ? deletedMirror(options) : mirrorMessage(threadId, rawMessage, options),
    );
  }
  const prev = mirrorTails.get(threadId) ?? Promise.resolve();
  // Strict submitted writes reject on failure; a failed predecessor must not
  // stall the normal best-effort run-end writes or the next submission.
  const run = prev.then(
    () => mirrorOne(threadId, message, options),
    () => mirrorOne(threadId, message, options),
  );
  mirrorTails.set(threadId, run);
  // GC: drop the map entry once this settles and nothing newer was queued.
  const cleanup = () => {
    if (mirrorTails.get(threadId) === run) mirrorTails.delete(threadId);
  };
  void run.then(cleanup, cleanup);
  // A delete begun while the strict write was in flight must settle before
  // its caller may dispatch chat. Keep this wait outside mirrorTails: DELETE
  // drains the write itself, so putting its own gate in the tail deadlocks.
  return options.required
    ? run.then(async () => {
        await requireUndeletedThread(threadId);
        requireMirrorOwner(options);
      })
    : run;
}
